import React from 'react';

export type OnboardingStep = 'authorize' | 'contextualize' | 'policies' | 'rubrics' | 'upcoming-dunning';

interface OnboardingNavigationProps {
  selectedStep: OnboardingStep;
  onStepChange: (step: OnboardingStep) => void;
  onboardingStatus?: any;
  completedSteps?: Set<string>;
  publicMode?: boolean;
}

const OnboardingNavigation: React.FC<OnboardingNavigationProps> = ({
  selectedStep,
  onStepChange,
  onboardingStatus,
  completedSteps = new Set(),
  publicMode = false
}) => {
  const steps: Array<{ id: OnboardingStep; icon: string; title: string; isCompleted: boolean; isAvailable: boolean; statusIcon: string }> = [
    {
      id: 'authorize',
      icon: '🔗',
      title: '1. Connect accounting',
      isCompleted: onboardingStatus?.xero_authorization?.is_authorized,
      isAvailable: publicMode || true,
      statusIcon: onboardingStatus?.xero_authorization?.is_authorized ? '✅' : '⏳'
    },
    {
      id: 'contextualize',
      icon: '❓',
      title: '2. Answer a few questions',
      isCompleted: completedSteps.has('contextualize'),
      isAvailable: publicMode || onboardingStatus?.xero_authorization?.is_authorized,
      statusIcon: completedSteps.has('contextualize') 
        ? '✅' 
        : (publicMode || onboardingStatus?.xero_authorization?.is_authorized)
          ? '🚀' 
          : '-'
    },
    {
      id: 'policies',
      icon: '📋',
      title: '3. Personalize Policies',
      isCompleted: completedSteps.has('policies'),
      isAvailable: publicMode || onboardingStatus?.xero_authorization?.is_authorized,
      statusIcon: completedSteps.has('policies') 
        ? '✅' 
        : (publicMode || onboardingStatus?.xero_authorization?.is_authorized)
          ? '🚀' 
          : '-'
    },
    {
      id: 'rubrics',
      icon: '📊',
      title: '4. Understand Rubrics',
      isCompleted: false,
      isAvailable: publicMode || onboardingStatus?.xero_authorization?.is_authorized,
      statusIcon: (publicMode || onboardingStatus?.xero_authorization?.is_authorized) ? '⏳' : '-'
    },
    {
      id: 'upcoming-dunning',
      icon: '📅',
      title: '5. Upcoming Dunning',
      isCompleted: false,
      isAvailable: publicMode || onboardingStatus?.xero_authorization?.is_authorized,
      statusIcon: (publicMode || onboardingStatus?.xero_authorization?.is_authorized) ? '🚀' : '-'
    }
  ];

  return (
    <div className="bg-white rounded-lg sm:rounded-xl shadow p-3 sm:p-4 lg:sticky lg:top-4">
      <nav className="space-y-1 sm:space-y-2">
        {steps.map((step) => {
          const isSelected = selectedStep === step.id;
          const isDisabled = !step.isAvailable;
          
          return (
            <button
              key={step.id}
              onClick={() => !isDisabled && onStepChange(step.id)}
              disabled={isDisabled}
              className={`w-full text-left px-3 sm:px-4 py-2 sm:py-3 rounded-md sm:rounded-lg border transition-colors ${
                isSelected
                  ? 'bg-blue-50 border-blue-200 text-blue-700'
                  : isDisabled
                    ? 'bg-gray-50 border-gray-200 text-gray-400 cursor-not-allowed'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center min-w-0 flex-1">
                  <span className="text-gray-400 mr-2 sm:mr-3 text-sm sm:text-base flex-shrink-0">{step.icon}</span>
                  <span className="font-medium text-sm sm:text-base truncate">{step.title}</span>
                </div>
                <span className={`flex-shrink-0 ml-2 ${
                  step.statusIcon === '✅' ? 'text-green-600' :
                  step.statusIcon === '🚀' ? 'text-yellow-500' :
                  step.statusIcon === '⏳' ? 'text-gray-400' :
                  'text-gray-300'
                }`}>
                  {step.statusIcon}
                </span>
              </div>
            </button>
          );
        })}
      </nav>
    </div>
  );
};

export default OnboardingNavigation;
